import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog } from '@koridor/design-system';
import { api, ApiError } from '@koridor/api-client';
import { useGame } from '../contexts/GameContext';
import { dismissRematch, isRematchDismissed, rematchSourceId } from '../lib/rematch';
import { buildGameLaunch, launchGame } from '../lib/launch-game';
import { enterRematchOnce, onRematchMatched } from '../lib/enter-rematch';
import { enterInviteOnce, onInviteAccepted } from '../lib/enter-invite';

type PendingInvite = {
  id: string;
  fromUser: { id: string; nickname: string };
  matchConfig: { boardSize?: number; ranked?: boolean };
};

type RematchOffer = {
  matchId: string;
  fromUser: { id?: string; nickname?: string };
  boardSize?: number;
  ranked?: boolean;
  mode?: 'online' | 'friend';
};

export function GlobalGameDialogs() {
  const navigate = useNavigate();
  const { setGameCfg } = useGame();
  const [pendingInvite, setPendingInvite] = React.useState<PendingInvite | null>(null);
  const [inviteBusy, setInviteBusy] = React.useState(false);
  const [inviteError, setInviteError] = React.useState('');
  const [rematchOffer, setRematchOffer] = React.useState<RematchOffer | null>(null);
  const [rematchBusy, setRematchBusy] = React.useState(false);
  const [rematchError, setRematchError] = React.useState('');

  React.useEffect(() => {
    if (!api.isAuthenticated) return;
    void api.getPendingInvites().then((rows) => {
      if (rows[0]) setPendingInvite(rows[0]);
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    const offInvite = api.on('invite.received', (payload: unknown) => {
      const p = payload as { invite?: { id: string; matchConfig?: { boardSize?: number; ranked?: boolean } }; fromUser?: { id: string; nickname: string } };
      if (!p?.invite?.id || !p.fromUser) return;
      setPendingInvite({
        id: p.invite.id,
        fromUser: p.fromUser,
        matchConfig: p.invite.matchConfig ?? { boardSize: 9 },
      });
    });
    const offRematch = api.on('rematch.offered', (payload: unknown) => {
      const p = payload as RematchOffer & { sourceMatchId?: string };
      const matchId = rematchSourceId(p);
      if (!matchId || isRematchDismissed(matchId)) return;
      setRematchOffer({ ...p, matchId });
    });
    const offCancelled = api.on('rematch.cancelled', (payload: unknown) => {
      const p = payload as { matchId?: string };
      if (p?.matchId) dismissRematch(p.matchId);
      setRematchOffer(null);
    });
    const offMatched = onRematchMatched((p) => {
      setRematchOffer(null);
      enterRematchOnce(p, navigate, setGameCfg);
    });
    const offInviteAccepted = onInviteAccepted((p) => {
      if (!p?.matchId) return;
      enterInviteOnce(p, navigate, setGameCfg);
    });
    return () => { offInvite(); offRematch(); offCancelled(); offMatched(); offInviteAccepted(); };
  }, [setGameCfg, navigate]);

  async function acceptInvite() {
    if (!pendingInvite) return;
    setInviteBusy(true);
    setInviteError('');
    try {
      const res = await api.acceptInvite(pendingInvite.id);
      setPendingInvite(null);
      void launchGame(buildGameLaunch({
        size: (pendingInvite.matchConfig.boardSize ?? 9) as 7 | 9,
        ranked: pendingInvite.matchConfig.ranked ?? false,
        local: false,
        mode: 'friend',
        matchId: res.matchId,
        opponent: pendingInvite.fromUser.nickname,
        opponentId: pendingInvite.fromUser.id,
      }), navigate, setGameCfg);
    } catch (e) {
      setInviteError(e instanceof ApiError ? e.message : 'Не удалось принять приглашение');
    } finally {
      setInviteBusy(false);
    }
  }

  async function declineInvite() {
    if (!pendingInvite) return;
    setInviteBusy(true);
    try {
      await api.declineInvite(pendingInvite.id);
      setPendingInvite(null);
    } catch (e) {
      setInviteError(e instanceof ApiError ? e.message : 'Не удалось отклонить');
    } finally {
      setInviteBusy(false);
    }
  }

  async function acceptRematch() {
    if (!rematchOffer) return;
    setRematchBusy(true);
    setRematchError('');
    const offer = rematchOffer;
    try {
      const res = await api.requestRematch(offer.matchId);
      setRematchOffer(null);
      if (res.matched && res.matchId) {
        enterRematchOnce({
          matchId: res.matchId,
          opponent: res.opponent ?? { nickname: offer.fromUser?.nickname, id: offer.fromUser?.id },
          boardSize: res.boardSize ?? offer.boardSize,
          ranked: res.ranked ?? offer.ranked,
          mode: res.mode ?? offer.mode,
        }, navigate, setGameCfg);
        return;
      }
      navigate(`/result?rematchFrom=${offer.matchId}`);
    } catch (e) {
      setRematchError(e instanceof ApiError ? e.message : 'Не удалось принять реванш');
    } finally {
      setRematchBusy(false);
    }
  }

  async function declineRematch() {
    if (!rematchOffer) return;
    setRematchBusy(true);
    const matchId = rematchOffer.matchId;
    try {
      await api.cancelRematch(matchId);
    } catch {
      /* server may already have closed rematch */
    } finally {
      dismissRematch(matchId);
      setRematchOffer(null);
      setRematchBusy(false);
    }
  }

  const boardSize = pendingInvite?.matchConfig.boardSize ?? 9;
  const inviteRanked = pendingInvite?.matchConfig.ranked;

  return (
    <>
      <Dialog open={!!pendingInvite} title="Приглашение в игру" onClose={() => !inviteBusy && setPendingInvite(null)}
        actions={(
          <>
            <Button variant="secondary" fullWidth disabled={inviteBusy} onClick={declineInvite}>Отклонить</Button>
            <Button variant="primary" fullWidth disabled={inviteBusy} onClick={acceptInvite}>
              {inviteBusy ? '…' : 'Принять'}
            </Button>
          </>
        )}>
        {pendingInvite && (
          <>
            <strong>{pendingInvite.fromUser.nickname}</strong> приглашает вас в партию {boardSize}×{boardSize}
            {inviteRanked ? ' (рейтинговая)' : ''}.
            {inviteError && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 13 }}>{inviteError}</div>}
          </>
        )}
      </Dialog>

      <Dialog open={!!rematchOffer} title="Реванш" onClose={() => { if (!rematchBusy) void declineRematch(); }}
        actions={(
          <>
            <Button variant="secondary" fullWidth disabled={rematchBusy} onClick={declineRematch}>Отклонить</Button>
            <Button variant="primary" fullWidth disabled={rematchBusy} onClick={acceptRematch}>
              {rematchBusy ? '…' : 'Принять'}
            </Button>
          </>
        )}>
        {rematchOffer && (
          <>
            <strong>{rematchOffer.fromUser?.nickname ?? 'Соперник'}</strong> предлагает реванш.
            {rematchError && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 13 }}>{rematchError}</div>}
          </>
        )}
      </Dialog>
    </>
  );
}
